import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  filesApi,
  FileType as FileTypeEnum,
  getOpenableFileUrl,
  toS3SafeFileName,
} from 'src/api/files';
import { informationApi, InformationDto } from 'src/api/information';
import {
  DEFAULT_TEAM_GROUP_NAME,
  parseTeamGroupError,
  TeamGroupDto,
  teamGroupsApi,
} from 'src/api/teamGroups';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { useCircleRoleView } from 'src/hooks/CircleRoleView/useCircleRoleView';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCurrentUserId } from 'src/redux/slices/loggedUserSlice';
import { InformationEditor } from './components/InformationEditor';
import { InformationPreview } from './components/InformationPreview';
import styles from './Information.module.css';
import {
  ContactListField,
  ContactListMember,
  FileDetails,
  InformationContentType,
  InformationProps,
  InformationSection,
  Mode,
  SectionValidationErrors,
} from './types';
import {
  getSkippedFilesMessage,
  validateUploadFile,
} from './utils/fileValidation';
import {
  applyDeclinedExclusionsToSections,
  extractContactSnapshot,
  loadCircleContactMembers,
  mergeDeclinedExclusions,
  resolveContactListMembers,
  toStoredContactMember,
} from './utils/contactListMembers';
import {
  createEditableTeam,
  EditableTeam,
  getContactListExclusions,
  getDuplicateMemberIds,
  getTeamEditorContacts,
  joinTeamsWithContacts,
  normalizeInformationContentTypes,
  toEditableTeams,
  toPutTeams,
} from './utils/teamGroups';
import { useOpenCircleFile } from './utils/useOpenCircleFile';
import {
  isRichTextMeaningfullyEmpty,
  normalizeQuillHtml,
} from './utils/richText';

const createSectionId = () =>
  `section-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const DEFAULT_CONTACT_FIELDS: ContactListField[] = ['name', 'phone', 'email'];

const buildSectionsFromForm = (
  form: InformationDto,
  fileDetails: Record<string, FileDetails>
): InformationSection[] => {
  return (form.sections ?? []).map((section: any) => ({
    id: createSectionId(),
    title: section.title ?? '',
    content: normalizeQuillHtml(section.content ?? ''),
    files: (section.fileIds ?? []).map((fileId: string) => ({
      id: fileId,
      name: fileDetails[fileId]?.name ?? '',
      contentType: fileDetails[fileId]?.contentType ?? '',
    })),
    contentTypes: normalizeInformationContentTypes(section.contentTypes),
    contactFields: section.contactFields ?? DEFAULT_CONTACT_FIELDS,
    excludedContactIds: section.excludedContactIds ?? [],
    teamGroupId: section.teamGroupId,
  }));
};

export const Information: React.FC<InformationProps> = ({
  circleId,
  onSave,
}) => {
  const [mode, setMode] = useState<Mode>('edit');
  const [sections, setSections] = useState<InformationSection[]>([]);
  const [savedForm, setSavedForm] = useState<InformationDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingBySection, setUploadingBySection] = useState<
    Record<string, number>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<
    Record<string, SectionValidationErrors>
  >({});
  const [fileDetails, setFileDetails] = useState<Record<string, FileDetails>>(
    {}
  );
  const [isFetchingFileDetails, setIsFetchingFileDetails] = useState(false);
  const [contactListMembers, setContactListMembers] = useState<
    ContactListMember[]
  >([]);
  const [collapsedPreviewSections, setCollapsedPreviewSections] = useState<
    Record<number, boolean>
  >({});
  const [savedTeamGroup, setSavedTeamGroup] = useState<TeamGroupDto | null>(
    null
  );
  const [draftTeams, setDraftTeams] = useState<EditableTeam[]>([]);
  const [draftGroupName, setDraftGroupName] = useState(DEFAULT_TEAM_GROUP_NAME);
  const [isTeamGroupLoading, setIsTeamGroupLoading] = useState(true);

  const { isLeader, actualIsLeader } = useCircleRoleView(circleId);
  const { openFile, openingFileId } = useOpenCircleFile();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const snapshotContactMembers = useMemo(
    () => extractContactSnapshot(savedForm),
    [savedForm]
  );
  const visibleContactMembers = useMemo(
    () => resolveContactListMembers(contactListMembers, snapshotContactMembers),
    [contactListMembers, snapshotContactMembers]
  );
  const teamEditorContacts = useMemo(
    () =>
      getTeamEditorContacts(
        visibleContactMembers,
        getContactListExclusions(sections)
      ),
    [visibleContactMembers, sections]
  );
  const displayTeams = useMemo(
    () =>
      joinTeamsWithContacts(savedTeamGroup?.teams ?? [], visibleContactMembers),
    [savedTeamGroup, visibleContactMembers]
  );

  const applyTeamGroup = useCallback((group: TeamGroupDto | null) => {
    setSavedTeamGroup(group);
    setDraftGroupName(group?.name || DEFAULT_TEAM_GROUP_NAME);
    setDraftTeams(group ? toEditableTeams(group.teams) : []);
  }, []);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);

    const loadExistingForm = async () => {
      try {
        const existing = await informationApi.getFormForCircle(circleId);
        if (!isMounted) {
          return;
        }

        if (!existing) {
          setSavedForm(null);
          setMode('edit');
          return;
        }

        setSavedForm(existing);
        const hasSections = (existing.sections ?? []).length > 0;
        setMode(isLeader && !hasSections ? 'edit' : 'view');
        setSections(
          (existing.sections ?? []).map((section: any) => ({
            id: createSectionId(),
            title: section.title ?? '',
            content: normalizeQuillHtml(section.content ?? ''),
            files: (section.fileIds ?? []).map((fileId: string) => ({
              id: fileId,
              name: '',
              contentType: '',
            })),
            contentTypes: normalizeInformationContentTypes(
              section.contentTypes
            ),
            contactFields: section.contactFields ?? DEFAULT_CONTACT_FIELDS,
            excludedContactIds: section.excludedContactIds ?? [],
            teamGroupId: section.teamGroupId,
          }))
        );
      } catch (err) {
        console.error('Failed to load custom form:', err);
        if (isMounted) {
          setError('Could not load existing form.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadExistingForm();

    return () => {
      isMounted = false;
    };
  }, [circleId, isLeader]);

  useEffect(() => {
    let isMounted = true;

    loadCircleContactMembers(circleId, actualIsLeader)
      .then((members) => {
        if (!isMounted) {
          return;
        }

        setContactListMembers(members);
      })
      .catch((err) => {
        console.error('Failed to load members for contact list:', err);
        if (isMounted) {
          setContactListMembers([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [circleId, actualIsLeader]);

  useEffect(() => {
    setSections((prev) =>
      applyDeclinedExclusionsToSections(prev, visibleContactMembers)
    );
  }, [visibleContactMembers]);

  useEffect(() => {
    let isMounted = true;
    setIsTeamGroupLoading(true);
    applyTeamGroup(null);

    teamGroupsApi
      .getTeamGroup(circleId)
      .then((group) => {
        if (!isMounted) {
          return;
        }
        applyTeamGroup(group);
      })
      .catch((err) => {
        console.error('Failed to load team groups:', err);
        if (isMounted) {
          setError(parseTeamGroupError(err, 'get').message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsTeamGroupLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applyTeamGroup, circleId]);

  useEffect(() => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        files: section.files.map((f) => {
          const d = fileDetails[f.id];
          if (!d || (f.name && f.contentType)) {
            return f;
          }
          return {
            ...f,
            name: f.name || d.name,
            contentType: f.contentType || d.contentType,
          };
        }),
      }))
    );
  }, [fileDetails]);

  useEffect(() => {
    if (!savedForm?.sections) {
      return;
    }

    const next: Record<number, boolean> = {};
    savedForm.sections.forEach((_s: any, idx: number) => {
      next[idx] = true;
    });
    setCollapsedPreviewSections(next);
  }, [savedForm]);

  useEffect(() => {
    if (!error) {
      return;
    }
    toast.error(error);
  }, [error]);

  useEffect(() => {
    if (!savedForm?.sections) {
      setIsFetchingFileDetails(false);
      return;
    }

    const allIds = new Set<string>();
    savedForm.sections.forEach((section: any) => {
      (section.fileIds ?? []).forEach((id: string) => {
        if (id && (!fileDetails[id] || !fileDetails[id].url)) {
          allIds.add(id);
        }
      });
    });

    if (allIds.size === 0) {
      setIsFetchingFileDetails(false);
      return;
    }

    let isMounted = true;
    setIsFetchingFileDetails(true);

    Promise.all(
      Array.from(allIds).map(async (fileId) => {
        try {
          const file = await filesApi.getFile(fileId);
          if (!isMounted) {
            return;
          }
          setFileDetails((prev) => ({
            ...prev,
            [fileId]: {
              name: file.name,
              url: getOpenableFileUrl(file),
              contentType: file.contentType,
            },
          }));
        } catch (err) {
          console.error('Failed to load file details for', fileId, err);
        }
      })
    ).finally(() => {
      if (isMounted) {
        setIsFetchingFileDetails(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [savedForm, fileDetails]);

  const togglePreviewSection = (index: number) => {
    setCollapsedPreviewSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleEdit = () => {
    if (!savedForm) {
      return;
    }
    setError(null);
    setSuccess(null);
    setSections(buildSectionsFromForm(savedForm, fileDetails));
    setMode('edit');
  };

  const handleCancelEdit = () => {
    setError(null);
    setSuccess(null);
    applyTeamGroup(savedTeamGroup);
    setMode('view');
  };

  const handleAddSection = () => {
    setSections((prev) => [
      {
        id: createSectionId(),
        title: '',
        content: '',
        files: [],
        contentTypes: ['text'],
        contactFields: DEFAULT_CONTACT_FIELDS,
        excludedContactIds: [],
      },
      ...prev,
    ]);
  };

  const handleRemoveSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    setSectionErrors((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const updateSection = useCallback(
    (id: string, patch: Partial<Omit<InformationSection, 'id'>>) => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === id ? { ...section, ...patch } : section
        )
      );
    },
    []
  );

  const handleSectionTitleChange = (id: string, value: string) => {
    updateSection(id, { title: value });
    setSectionErrors((prev) => {
      const current = prev[id];
      if (!current?.title) {
        return prev;
      }
      const nextEntry = { ...current };
      delete nextEntry.title;
      if (!nextEntry.content) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: nextEntry };
    });
  };

  const handleSectionContentChange = (id: string, value: string) => {
    updateSection(id, { content: value });
    setSectionErrors((prev) => {
      const current = prev[id];
      if (!current?.content) {
        return prev;
      }
      const nextEntry = { ...current };
      delete nextEntry.content;
      if (!nextEntry.title) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: nextEntry };
    });
  };

  const handleAddContent = (
    sectionId: string,
    contentType: InformationContentType
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId &&
        !(section.contentTypes ?? ['text', 'files']).includes(contentType)
          ? {
              ...section,
              contentTypes: [
                ...(section.contentTypes ?? ['text', 'files']),
                contentType,
              ],
              ...(contentType === 'contactList'
                ? {
                    excludedContactIds: mergeDeclinedExclusions(
                      section.excludedContactIds,
                      visibleContactMembers
                    ),
                  }
                : {}),
            }
          : section
      )
    );
    if (contentType === 'teams') {
      setDraftTeams((prev) =>
        prev.length > 0
          ? prev
          : [createEditableTeam({ index: 0 }), createEditableTeam({ index: 1 })]
      );
    }
  };

  const handleRemoveContent = (
    sectionId: string,
    contentType: InformationContentType
  ) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        return {
          ...section,
          contentTypes: (section.contentTypes ?? ['text', 'files']).filter(
            (type) => type !== contentType
          ),
          ...(contentType === 'text' ? { content: '' } : {}),
          ...(contentType === 'files' ? { files: [] } : {}),
        };
      })
    );

    if (contentType === 'text') {
      setSectionErrors((prev) => {
        const current = prev[sectionId];
        if (!current?.content) {
          return prev;
        }
        const nextEntry = { ...current };
        delete nextEntry.content;
        return { ...prev, [sectionId]: nextEntry };
      });
    }
  };

  const handleContactFieldsChange = (
    sectionId: string,
    fields: ContactListField[]
  ) => {
    updateSection(sectionId, { contactFields: fields });
  };

  const handleExcludedContactsChange = (
    sectionId: string,
    excludedContactIds: string[]
  ) => {
    updateSection(sectionId, {
      excludedContactIds: mergeDeclinedExclusions(
        excludedContactIds,
        visibleContactMembers
      ),
    });
  };

  const getSectionValidationErrors = useCallback(
    (sectionsToValidate: InformationSection[]) => {
      const nextErrors: Record<string, SectionValidationErrors> = {};
      sectionsToValidate.forEach((section) => {
        const titleMissing = section.title.trim().length === 0;
        if (!titleMissing) {
          return;
        }
        nextErrors[section.id] = {
          ...(titleMissing ? { title: 'Title is required.' } : {}),
        };
      });
      return nextErrors;
    },
    []
  );

  const handleSectionFilesChange = async (
    id: string,
    fileList: FileList | null
  ) => {
    setError(null);

    if (!fileList || fileList.length === 0) {
      return;
    }

    const browserFiles = Array.from(fileList);
    const unsupportedFiles: string[] = [];
    let oversizedFilesCount = 0;
    const validFiles: File[] = [];
    for (const file of browserFiles) {
      const validationError = validateUploadFile(file);
      if (validationError) {
        if (validationError === 'unsupported file type') {
          unsupportedFiles.push(file.name);
        } else if (validationError === 'file is larger than 150MB') {
          oversizedFilesCount += 1;
        }
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      const skippedMessage = getSkippedFilesMessage(
        unsupportedFiles,
        oversizedFilesCount
      );
      setError(skippedMessage || null);
      return;
    }

    setUploadingBySection((prev) => ({
      ...prev,
      [id]: (prev[id] ?? 0) + validFiles.length,
    }));

    const uploadResults = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const { uploadURL, file_id } = await filesApi.initUpload({
            name: toS3SafeFileName(file.name),
            contentType: file.type,
            type: FileTypeEnum.DocumentAttachment,
          });

          await fetch(uploadURL, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type,
            },
            body: file,
          });

          try {
            const uploaded = await filesApi.getFile(file_id);
            setFileDetails((prev) => ({
              ...prev,
              [file_id]: {
                name: uploaded.name ?? file.name,
                url: getOpenableFileUrl(uploaded),
                contentType: uploaded.contentType ?? file.type,
              },
            }));
          } catch (err) {
            console.warn('File metadata not ready yet for', file_id, err);
            setFileDetails((prev) => ({
              ...prev,
              [file_id]: {
                name: file.name,
                contentType: file.type,
              },
            }));
          }

          setSections((prev) =>
            prev.map((section) =>
              section.id === id
                ? {
                    ...section,
                    files: [
                      ...section.files,
                      {
                        id: file_id,
                        name: file.name,
                        contentType: file.type,
                      },
                    ],
                  }
                : section
            )
          );

          setFileDetails((prev) => ({
            ...prev,
            [file_id]: {
              name: file.name,
              contentType: file.type,
            },
          }));

          return true;
        } catch (err) {
          console.error('Failed to upload file for section', id, err);
          return false;
        } finally {
          setUploadingBySection((prev) => {
            const current = prev[id] ?? 0;
            if (current <= 1) {
              const { [id]: _removed, ...rest } = prev;
              return rest;
            }
            return {
              ...prev,
              [id]: current - 1,
            };
          });
        }
      })
    );

    const hadUploadError = uploadResults.some((ok) => !ok);

    const skippedMessage = getSkippedFilesMessage(
      unsupportedFiles,
      oversizedFilesCount
    );
    const messageParts = [skippedMessage];
    if (hadUploadError) {
      messageParts.push('One or more supported files failed during upload.');
    }
    const finalMessage = messageParts.filter(Boolean).join(' ');
    setError(finalMessage || null);
  };

  const handleRemoveFile = (sectionId: string, fileIndex: number) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              files: section.files.filter((_, idx) => idx !== fileIndex),
            }
          : section
      )
    );
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    setSections((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleMoveSectionUp = (index: number) => {
    moveSection(index, index - 1);
  };

  const handleMoveSectionDown = (index: number) => {
    moveSection(index, index + 1);
  };

  const canSave = useMemo(() => {
    if (sections.length === 0) {
      return true;
    }
    return sections.some(
      (s) =>
        s.title.trim().length > 0 || !isRichTextMeaningfullyEmpty(s.content)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, savedForm]);
  const isAnyUploading = useMemo(
    () => Object.keys(uploadingBySection).length > 0,
    [uploadingBySection]
  );

  const handleSaveForm = async (): Promise<void> => {
    if (!canSave || isSaving || isAnyUploading) {
      return;
    }

    const validationErrors = getSectionValidationErrors(sections);
    if (Object.keys(validationErrors).length > 0) {
      setSectionErrors(validationErrors);
      toast.error('Please fill in all required fields in each section.');
      setSuccess(null);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    setSectionErrors({});

    try {
      let teamGroupId = savedTeamGroup?._id;
      const hasTeamsContent = sections.some((section) =>
        section.contentTypes.includes('teams')
      );

      if (hasTeamsContent) {
        const duplicates = getDuplicateMemberIds(draftTeams);
        if (duplicates.length > 0) {
          toast.error('Each person can only be on one team.');
          setIsSaving(false);
          return;
        }

        const savedGroup = await teamGroupsApi.putTeamGroup(circleId, {
          name: draftGroupName.trim() || DEFAULT_TEAM_GROUP_NAME,
          teams: toPutTeams(draftTeams),
        });
        applyTeamGroup(savedGroup);
        teamGroupId = savedGroup._id;
      }

      const backendSections = sections.map((section) => ({
        title: section.title.trim(),
        content: normalizeQuillHtml(section.content || ''),
        fileIds: section.files.map((f) => f.id),
        contentTypes: section.contentTypes,
        contactFields: section.contactFields,
        excludedContactIds: mergeDeclinedExclusions(
          section.excludedContactIds,
          visibleContactMembers
        ),
        ...(section.contentTypes.includes('contactList')
          ? {
              contactMembers: visibleContactMembers.map(toStoredContactMember),
            }
          : {}),
        ...(section.contentTypes.includes('teams') && teamGroupId
          ? { teamGroupId }
          : {}),
      }));

      const saved = await informationApi.createForm({
        circleId,
        sections: backendSections,
      });
      setSavedForm(saved);

      if (onSave) {
        onSave({
          circleId,
          sections,
        });
      }

      setSuccess('Form saved successfully.');
      if (isLeader && backendSections.length === 0) {
        setMode('edit');
      } else {
        setMode('view');
      }
      console.log('Saved information form:', saved);
    } catch (err) {
      console.error('Failed to save information form:', err);
      setError(parseTeamGroupError(err, 'put').message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleSaveForm();
  };

  const hasSavedSections = (savedForm?.sections ?? []).length > 0;
  const showInitialLoader = (isLoading || isTeamGroupLoading) && !savedForm;

  return (
    <div className={styles.container}>
      {showInitialLoader && (
        <div className={styles.previewLoading}>
          <MaterialCircularProgress height="auto" size={36} />
        </div>
      )}

      {!showInitialLoader &&
        savedForm &&
        mode === 'view' &&
        hasSavedSections && (
          <div className={styles.previewContainer}>
            <div>
              <InformationPreview
                savedForm={savedForm}
                collapsedPreviewSections={collapsedPreviewSections}
                onTogglePreviewSection={togglePreviewSection}
                isLeader={isLeader}
                onEdit={handleEdit}
                fileDetails={fileDetails}
                isFetchingFileDetails={isFetchingFileDetails}
                contactListMembers={visibleContactMembers}
                openingFileId={openingFileId}
                onOpenFile={openFile}
                teams={displayTeams}
                currentUserId={currentUserId}
                circleId={circleId}
              />
            </div>
          </div>
        )}

      {!showInitialLoader && isLeader && mode === 'edit' && (
        <InformationEditor
          isLoading={isLoading}
          success={success}
          savedFormExists={!!savedForm}
          isSaving={isSaving}
          canSave={canSave}
          isAnyUploading={isAnyUploading}
          sections={sections}
          uploadingBySection={uploadingBySection}
          sectionErrors={sectionErrors}
          fileDetails={fileDetails}
          isFetchingFileDetails={isFetchingFileDetails}
          contactListMembers={visibleContactMembers}
          onSubmit={handleSubmit}
          onCancelEdit={handleCancelEdit}
          onAddSection={handleAddSection}
          onMoveSectionUp={handleMoveSectionUp}
          onMoveSectionDown={handleMoveSectionDown}
          onRemoveSection={handleRemoveSection}
          onSectionTitleChange={handleSectionTitleChange}
          onSectionContentChange={handleSectionContentChange}
          onSectionFilesChange={handleSectionFilesChange}
          onRemoveFile={handleRemoveFile}
          onAddContent={handleAddContent}
          onRemoveContent={handleRemoveContent}
          onContactFieldsChange={handleContactFieldsChange}
          onExcludedContactsChange={handleExcludedContactsChange}
          openingFileId={openingFileId}
          onOpenFile={openFile}
          enableTeams
          teamGroupName={draftGroupName}
          onTeamGroupNameChange={setDraftGroupName}
          draftTeams={draftTeams}
          previousTeams={savedTeamGroup?.previousTeams ?? []}
          onDraftTeamsChange={setDraftTeams}
          teamContacts={teamEditorContacts}
          currentUserId={currentUserId}
          circleId={circleId}
          isLeader={isLeader}
        />
      )}

      {!showInitialLoader && !isLeader && !hasSavedSections && (
        <div className={styles.previewContainer}>
          <div>
            <p className={styles.previewEmpty}>No sections stored yet.</p>
          </div>
        </div>
      )}
    </div>
  );
};
